import React, { useMemo } from 'react';
import type { TargetNode } from '../../../../types/targetUniverse';

// Reference colour palette — DO NOT CHANGE
const C = {
  textMuted: '#64748b',
  textDim: '#475569',
  accent: '#3b82f6',
  projectTarget: '#22d3ee',
  safe: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
};

const riskColor = (pIC50: number, projectPIC50: number): string => {
  const ratio = Math.pow(10, projectPIC50 - pIC50);
  if (ratio > 100) return C.safe;
  if (ratio > 10) return C.warning;
  return C.danger;
};

interface Props {
  targets: TargetNode[];
}

const CX = 140;
const CY = 130;
const MAX_R = 100;

const SelectivityRadar: React.FC<Props> = ({ targets }) => {
  const topTargets = useMemo(() =>
    [...targets]
      .filter(t => t.pIC50 != null)
      .sort((a, b) => (b.pIC50 || 0) - (a.pIC50 || 0))
      .slice(0, 10),
    [targets]
  );

  const n = topTargets.length;
  const projectPIC50 = targets.find(t => t.is_primary)?.pIC50 || 8;

  if (n === 0) {
    return (
      <div style={{
        color: C.textDim, fontSize: 12,
        fontFamily: "'JetBrains Mono', monospace",
        textAlign: 'center', padding: '40px 20px',
      }}>
        Not enough target data for radar view
      </div>
    );
  }

  const angleStep = (2 * Math.PI) / n;

  const points = topTargets.map((t, i) => {
    const val = (t.pIC50 || 0) / 10; // Normalise 0–1 (0→0, 10→1)
    const angle = -Math.PI / 2 + i * angleStep;
    return {
      name: t.target_name,
      val,
      pIC50: t.pIC50 || 0,
      x: CX + Math.cos(angle) * MAX_R * val,
      y: CY + Math.sin(angle) * MAX_R * val,
      lx: CX + Math.cos(angle) * (MAX_R + 20),
      ly: CY + Math.sin(angle) * (MAX_R + 20),
      isProject: t.is_primary,
    };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <div>
      <svg viewBox="0 0 280 280" style={{ width: '100%', maxWidth: 300, background: 'transparent' }}>
        {/* Grid rings (polygons) */}
        {[0.25, 0.5, 0.75, 1].map(r => (
          <polygon key={r}
            points={topTargets.map((_, i) => {
              const angle = -Math.PI / 2 + i * angleStep;
              return `${CX + Math.cos(angle) * MAX_R * r},${CY + Math.sin(angle) * MAX_R * r}`;
            }).join(' ')}
            fill="none" stroke="rgba(59,130,246,0.1)" strokeWidth={1}
          />
        ))}

        {/* Axes */}
        {topTargets.map((_, i) => {
          const angle = -Math.PI / 2 + i * angleStep;
          return (
            <line key={i} x1={CX} y1={CY}
              x2={CX + Math.cos(angle) * MAX_R}
              y2={CY + Math.sin(angle) * MAX_R}
              stroke="rgba(59,130,246,0.15)" strokeWidth={1} />
          );
        })}

        {/* Filled polygon */}
        <path d={pathD} fill="rgba(59,130,246,0.12)" stroke={C.accent} strokeWidth={1.5} />

        {/* Points + labels */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={p.isProject ? 5 : 3.5}
              fill={p.isProject ? C.projectTarget : riskColor(p.pIC50, projectPIC50)}
              stroke={p.isProject ? C.projectTarget : 'none'} strokeWidth={1} />
            <text x={p.lx} y={p.ly + 4} textAnchor="middle"
              fill={p.isProject ? C.projectTarget : C.textMuted} fontSize={9}
              fontWeight={p.isProject ? 700 : 400}
              fontFamily="'JetBrains Mono', monospace">
              <title>{p.name}</title>
              {p.name.length > 16 ? p.name.substring(0, 13) + '...' : p.name}
            </text>
          </g>
        ))}

        {/* pIC50 scale labels */}
        <text x={CX + 4} y={CY - MAX_R * 0.5 - 2} fill={C.textDim} fontSize={8}
          fontFamily="'JetBrains Mono', monospace">5</text>
        <text x={CX + 4} y={CY - MAX_R * 0.75 - 2} fill={C.textDim} fontSize={8}
          fontFamily="'JetBrains Mono', monospace">7.5</text>
      </svg>
      <div style={{
        color: C.textMuted, fontSize: 10, textAlign: 'center', marginTop: 8,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        Selectivity profile — axes show pIC50 per target
      </div>
    </div>
  );
};

export default SelectivityRadar;
